// src/ProtectedRoute.js
import { useContext } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { AuthContext } from './context/AuthContext';

/**
 * <ProtectedRoute requiredRole="admin" />
 *
 * Guards child routes:
 *   • Redirects to /login if not authenticated
 *   • Blocks pending users (shows pending approval message)
 *   • Redirects to /setup if role mismatch (basic users go to setup page)
 *   • Otherwise renders <Outlet> and passes { userRole, user }
 */
const ProtectedRoute = ({ requiredRole }) => {
  const { isAuthenticated, user } = useContext(AuthContext);

  /* -------- 1. Not logged in → /login ---------- */
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  /* -------- 2. Pending users → blocked ---------- */
  if (user?.role === 'pending') {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '40px 20px'
      }}>
        <div style={{
          background: 'white',
          padding: '60px 40px',
          borderRadius: '16px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          textAlign: 'center',
          maxWidth: '600px'
        }}>
          <h1 style={{ color: '#667eea', marginBottom: '16px' }}>Account Pending Approval</h1>
          <p style={{ fontSize: '16px', color: '#555', marginBottom: '24px' }}>
            Your account is awaiting approval from a system administrator.
          </p>
          <p style={{ fontSize: '14px', color: '#777', background: '#f5f5f5', padding: '16px', borderRadius: '8px' }}>
            You will receive access to the system once your account has been reviewed and approved.
            Please contact your administrator if you have any questions.
          </p>
        </div>
      </div>
    );
  }

  /* -------- 3. Role guard (optional) ----------- */
  if (requiredRole) {
    const isAdmin = user?.role === 'admin' || user?.isSuperAdmin;
    const isSales = user?.role === 'sales';

    // Handle array of roles or single role
    const requiredRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];

    // Check if user has any of the required roles
    let hasAccess = false;
    for (const role of requiredRoles) {
      if (role === 'superadmin' && user?.isSuperAdmin) hasAccess = true; // super admin ONLY
      if (role === 'admin' && isAdmin) hasAccess = true;
      if (role === 'sales' && (isSales || isAdmin)) hasAccess = true; // sales, admin, or super admin
    }

    if (!hasAccess) {
      // Users without required role get redirected to homepage
      return <Navigate to="/main-homepage" replace />;
    }
  }

  /* -------- 4. Happy path ---------------------- */
  return <Outlet context={{ userRole: user?.role, user }} />;
};

export default ProtectedRoute;
